import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaSearch, FaEdit, FaTrash } from 'react-icons/fa';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  Paper,
  InputAdornment,
  ListSubheader,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import 'dayjs/locale/en-gb';
import { employeesApi, organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { calculateDuration } from '../../../utils/dateUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { ORGANIZATIONAL_STATUSES } from '../../../constants/organizationalStatuses';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  BackButton,
  HeaderButton,
} from '../../common/styles';
import { Content, CurrentPositionContainer, TimelineContainer } from './styles';
import dayjs from 'dayjs';
import EditPositionModal from './EditPositionModal';
import EditGradeModal from './EditGradeModal';
import AddGradeModal from './AddGradeModal';
import ConfirmationModal from '../../common/ConfirmationModal';
import { disableOverlappingStart, startDateHint } from '../../../utils/positionHistoryDates';
import { formatDateOnly, fromPickerValue, toLocalDay } from '../../../utils/dateOnly';

const EmployeePositionHistory = ({ onBack, scrollState, onEmployeeUpdate }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);
  const [positions, setPositions] = useState([]);
  const [positionsByOrgUnitType, setPositionsByOrgUnitType] = useState([]);
  const [positionSearchQuery, setPositionSearchQuery] = useState('');
  const [grades, setGrades] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEntry, setNewEntry] = useState({
    positionId: '',
    gradeId: null,
    startDate: null,
    reason: ''
  });
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  // ADR-169 D8 — a refusal (overlap, nothing to change) is shown inside the
  // dialog, which stays open.
  const [addError, setAddError] = useState(null);

  // Edit/Delete state for positions
  const [editPositionModalOpen, setEditPositionModalOpen] = useState(false);
  const [positionToEdit, setPositionToEdit] = useState(null);
  const [deletePositionConfirmOpen, setDeletePositionConfirmOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);

  // Edit/Delete state for grades
  const [editGradeModalOpen, setEditGradeModalOpen] = useState(false);
  const [gradeToEdit, setGradeToEdit] = useState(null);
  const [deleteGradeConfirmOpen, setDeleteGradeConfirmOpen] = useState(false);
  const [gradeToDelete, setGradeToDelete] = useState(null);

  // Add grade state
  const [addGradeModalOpen, setAddGradeModalOpen] = useState(false);
  const [positionForNewGrade, setPositionForNewGrade] = useState(null);

  // Use organizationalStatus from navigation state if available, otherwise from API response
  const organizationalStatus = location.state?.organizationalStatus || employeeData?.employee?.organizationalStatus;

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (isSelectOpen && newEntry.positionId) {
      // Give time for the select menu to render
      setTimeout(() => {
        const selectedElement = document.querySelector(`[data-value="${newEntry.positionId}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'center' });
        }
      }, 100);
    }
  }, [isSelectOpen, newEntry.positionId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [positionsData, gradesData, historyData] = await Promise.all([
        organizationApi.getPositionsByOrgUnitType(),
        organizationApi.getAllGrades(),
        employeesApi.getEmployeePositionHistory(id)
      ]);

      setEmployeeData(historyData);
      setPositionsByOrgUnitType(positionsData);
      // Keep the positions array for backward compatibility
      const allPositions = positionsData.reduce((acc, group) => [...acc, ...group.positions], []);
      setPositions(allPositions);
      setGrades(gradesData);
    } catch (err) {
      showAlert(err.message || 'Failed to load data', 'error', err);
      if (onBack) {
        onBack();
      } else {
        navigate('/employees');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filterPositions = (positions, query) => {
    if (!query) return positions;
    return positions.filter(position => 
      position.label.toLowerCase().includes(query.toLowerCase())
    );
  };

  const getFilteredPositionsByOrgUnitType = () => {
    return positionsByOrgUnitType.map(group => ({
      ...group,
      positions: filterPositions(group.positions, positionSearchQuery)
    })).filter(group => group.positions.length > 0);
  };

  const handleAddEntry = async () => {
    try {
      // Get selected position to check if it requires grade
      const selectedPosition = positions.find(p => p.id === newEntry.positionId);
      
      // Validate required fields
      if (!newEntry.positionId) {
        showAlert('Please select a position', 'error');
        return;
      }

      // Only validate grade if position requires it
      if (selectedPosition?.requiresGrade && !newEntry.gradeId) {
        showAlert('Please select a grade', 'error');
        return;
      }

      // ADR-143 §5.2 — the effective date is required, not defaulted: it is
      // the amendment's effective date and the history leg's start.
      if (!newEntry.startDate) {
        showAlert('Please select the start date — the day the change takes effect', 'error');
        return;
      }

      const payload = {
        positionId: newEntry.positionId,
        gradeId: newEntry.gradeId || null,
        startDate: fromPickerValue(newEntry.startDate),
        // Optional: recorded as the reason on the position amendment
        // (ADR-143 §5); a standard text is used when left empty.
        ...(newEntry.reason?.trim() ? { reason: newEntry.reason.trim() } : {})
      };
      setAddError(null);
      const saved = await employeesApi.addPositionHistory(id, payload);
      showAlert('Position history entry added successfully', 'success');
      // ADR-169 D6 — saved, but something is worth a look (no contract covers
      // the start; a gap before the entry).
      const warnings = Array.isArray(saved?.warnings) ? saved.warnings : [];
      if (warnings.length > 0) {
        showAlert(warnings.map((w) => w.message).join(' · '), 'warning', null, 10000);
      }
      setShowAddModal(false);
      setNewEntry({
        positionId: '',
        gradeId: null,
        startDate: null,
        reason: ''
      });
      fetchData();
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (err) {
      setAddError(err.message || 'Failed to add position history entry');
    }
  };

  const handleCloseModal = () => {
    setAddError(null);
    setShowAddModal(false);
    setNewEntry({ positionId: '', gradeId: '', startDate: null, reason: '' });
  };

  // Position edit/delete handlers
  const handleEditPosition = (entry) => {
    setPositionToEdit(entry);
    setEditPositionModalOpen(true);
  };

  const handleDeletePositionClick = (entry) => {
    setPositionToDelete(entry);
    setDeletePositionConfirmOpen(true);
  };

  const handleDeletePositionConfirm = async () => {
    try {
      await employeesApi.deletePositionHistory(id, positionToDelete.id);
      showAlert('Position history deleted successfully', 'success');
      setDeletePositionConfirmOpen(false);
      setPositionToDelete(null);
      fetchData();
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (err) {
      showAlert(err.message || 'Failed to delete position history', 'error', err);
    }
  };

  // Grade edit/delete handlers
  const handleEditGrade = (gradeEntry, positionEntry) => {
    setGradeToEdit({ ...gradeEntry, positionLabel: positionEntry?.position?.label });
    setEditGradeModalOpen(true);
  };

  const handleDeleteGradeClick = (gradeEntry) => {
    setGradeToDelete(gradeEntry);
    setDeleteGradeConfirmOpen(true);
  };

  const handleDeleteGradeConfirm = async () => {
    try {
      await employeesApi.deleteGradeHistory(id, gradeToDelete.id);
      showAlert('Grade history deleted successfully', 'success');
      setDeleteGradeConfirmOpen(false);
      setGradeToDelete(null);
      fetchData();
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (err) {
      showAlert(err.message || 'Failed to delete grade history', 'error', err);
    }
  };

  const handlePositionEditSuccess = () => {
    fetchData();
    if (onEmployeeUpdate) onEmployeeUpdate();
  };

  const handleGradeEditSuccess = () => {
    fetchData();
    if (onEmployeeUpdate) onEmployeeUpdate();
  };

  // Add grade handler
  const handleAddGrade = (entry) => {
    setPositionForNewGrade(entry);
    setAddGradeModalOpen(true);
  };

  const handleAddGradeSuccess = () => {
    fetchData();
    if (onEmployeeUpdate) onEmployeeUpdate();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => {
              // ADR-165 — a caller (e.g. the Overlapping Position Legs report)
              // passes returnTo/returnLabel on location.state; honour it and
              // forward the rest so the caller can restore its filters. The pair
              // is consumed, as on the individual's page (ADR-113 addendum).
              const returnTo = location.state?.returnTo;
              if (returnTo) {
                const { returnTo: _consumedTo, returnLabel: _consumedLabel, ...restState } = location.state || {};
                navigate(returnTo, { state: restState });
              } else if (onBack) {
                onBack();
              } else {
                navigate(`/employees/${id}/edit`);
              }
            }}>
              <FaArrowLeft /> {location.state?.returnLabel ? `Back to ${location.state.returnLabel}` : 'Back to Individual'}
            </BackButton>
            <Title>Position & Grade History | {employeeData?.employee?.fullName}</Title>
          </HeaderLeft>
          {organizationalStatus !== ORGANIZATIONAL_STATUSES.RESIGNED && (
            <HeaderButton onClick={() => {
              setNewEntry({ positionId: '', gradeId: '', startDate: null, reason: '' });
              setAddError(null);
              setShowAddModal(true);
            }}>
              <FaPlus /> Add Position and Grade
            </HeaderButton>
          )}
        </Header>

        <Content>
          {/* Current Position & Grade */}
          <CurrentPositionContainer>
            <Typography variant="h6" sx={{ color: '#1E293B', fontWeight: 600 }}>
              Current Position & Grade
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
              <Box>
                <Typography sx={{ color: '#1E293B', fontWeight: 500 }}>
                  {employeeData?.employee?.currentPosition?.label || 'Not assigned'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: '#1E293B', fontWeight: 500 }}>
                  {employeeData?.employee?.currentGrade?.label || ''}
                </Typography>
              </Box>
            </Box>
          </CurrentPositionContainer>

          {/* History Timeline */}
          <TimelineContainer>
            <Typography variant="h6" sx={{ mb: 3, color: '#1E293B', fontWeight: 600 }}>
              History
            </Typography>
            {!employeeData?.history?.length ? (
              <EmptyState
                title="No History Found"
                description="No position history entries found for this individual."
              />
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 3,
                position: 'relative',
                width: '100%'
              }}>
                {(() => {
                  // Group entries into groups of 3 for rows
                  const rows = [];
                  for (let i = 0; i < employeeData.history.length; i += 3) {
                    rows.push(employeeData.history.slice(i, i + 3));
                  }
                  
                  return rows.map((row, rowIndex) => (
                    <Box
                      key={rowIndex}
                      sx={{
                        display: 'flex',
                        flexDirection: rowIndex % 2 === 0 ? 'row' : 'row-reverse',
                        gap: 3,
                        width: '100%',
                        position: 'relative',
                        marginBottom: rowIndex !== rows.length - 1 ? '24px' : 0,
                        '& > *': {
                          flex: '1 1 0',
                          width: 'calc((100% - 48px) / 3)', // Account for gaps (24px * 2)
                          minWidth: 'calc((100% - 48px) / 3)',
                          maxWidth: 'calc((100% - 48px) / 3)'
                        }
                      }}
                    >
                      {row.map((entry, index) => (
                        <Box
                          key={entry.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            position: 'relative',
                            // Horizontal connector between boxes in the same row
                            '&::after': index !== row.length - 1 ? {
                              content: '""',
                              position: 'absolute',
                              top: '50%',
                              ...(rowIndex % 2 === 0 ? {
                                right: '-24px',
                              } : {
                                left: '-24px',
                              }),
                              width: '48px',
                              height: '2px',
                              backgroundColor: '#2563EB',
                              transform: 'translateY(-50%)'
                            } : {},
                            // Vertical connector between rows - only for last box of each row
                            ...(rowIndex !== rows.length - 1 && index === row.length - 1 && {
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                bottom: '-42px',
                                left: '50%',
                                width: '2px',
                                height: '56px',
                                backgroundColor: '#2563EB',
                                transform: 'translateX(-50%)'
                              }
                            })
                          }}
                        >
                          <Paper
                            sx={{
                              p: 3,
                              ...(rowIndex % 2 === 0 ? {
                                borderLeft: `4px solid ${!entry.endDate ? '#22C55E' : '#2563EB'}`,
                              } : {
                                borderRight: `4px solid ${!entry.endDate ? '#22C55E' : '#2563EB'}`,
                                borderLeft: 'none',
                              }),
                              backgroundColor: '#FFFFFF',
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {entry.position.label}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {/* ADR-111 §6.4: a future leg is a SCHEDULED change — "Present"
                                      would repeat the exact misreading the ADR fixes */}
                                  {toLocalDay(entry.startDate)?.isAfter(dayjs(), 'day')
                                    ? `starts ${formatDateOnly(entry.startDate, 'DD/MM/YYYY')} (scheduled)`
                                    : `${formatDateOnly(entry.startDate, 'DD/MM/YYYY')} - ${entry.endDate ? formatDateOnly(entry.endDate, 'DD/MM/YYYY') : 'Present'}`}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                                  {calculateDuration(entry.startDate, entry.endDate)}
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                  {entry.position.requiresGrade && entry.gradeHistory.length === 0 && (
                                    <Typography variant="body2" sx={{ color: '#DC2626', fontStyle: 'italic', ml: rowIndex % 2 === 0 ? 2 : 0, mr: rowIndex % 2 === 0 ? 0 : 2 }}>
                                      No grade assigned. This position requires a grade.
                                    </Typography>
                                  )}
                                  {entry.gradeHistory.map((gradeEntry) => (
                                    <Box key={gradeEntry.id} sx={{
                                      ml: rowIndex % 2 === 0 ? 2 : 0,
                                      mr: rowIndex % 2 === 0 ? 0 : 2,
                                      mb: 1,
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      justifyContent: 'space-between'
                                    }}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                                          {gradeEntry.grade.label}
                                          <span style={{ marginLeft: '8px', color: '#94A3B8' }}>
                                            {toLocalDay(gradeEntry.startDate)?.isAfter(dayjs(), 'day')
                                              ? `(starts ${formatDateOnly(gradeEntry.startDate, 'DD/MM/YYYY')}, scheduled)`
                                              : `(${formatDateOnly(gradeEntry.startDate, 'DD/MM/YYYY')} - ${gradeEntry.endDate ? formatDateOnly(gradeEntry.endDate, 'DD/MM/YYYY') : 'Present'})`}
                                          </span>
                                          <span style={{ marginLeft: '8px', color: '#94A3B8', fontStyle: 'italic' }}>
                                            • {calculateDuration(gradeEntry.startDate, gradeEntry.endDate)}
                                          </span>
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: '#94A3B8' }}>
                                          Updated by {gradeEntry.updatedBy} on {dayjs(gradeEntry.updatedAt).format('DD/MM/YYYY')}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0, minWidth: 'fit-content' }}>
                                        <Tooltip title="Edit grade">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleEditGrade(gradeEntry, entry)}
                                            sx={{
                                              p: 0.5,
                                              color: '#64748B',
                                              '&:hover': { color: '#2563EB', backgroundColor: 'rgba(37, 99, 235, 0.1)' }
                                            }}
                                          >
                                            <FaEdit size={12} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete grade">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteGradeClick(gradeEntry)}
                                            sx={{
                                              p: 0.5,
                                              color: '#64748B',
                                              '&:hover': { color: '#DC2626', backgroundColor: 'rgba(220, 38, 38, 0.1)' }
                                            }}
                                          >
                                            <FaTrash size={12} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0, minWidth: 'fit-content' }}>
                                {entry.position.requiresGrade && (
                                  <Tooltip title="Add grade">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAddGrade(entry)}
                                      sx={{
                                        p: 0.5,
                                        color: '#64748B',
                                        '&:hover': { color: '#22C55E', backgroundColor: 'rgba(34, 197, 94, 0.1)' }
                                      }}
                                    >
                                      <FaPlus size={14} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Edit position">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditPosition(entry)}
                                    sx={{
                                      p: 0.5,
                                      color: '#64748B',
                                      '&:hover': { color: '#2563EB', backgroundColor: 'rgba(37, 99, 235, 0.1)' }
                                    }}
                                  >
                                    <FaEdit size={14} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete position">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeletePositionClick(entry)}
                                    sx={{
                                      p: 0.5,
                                      color: '#64748B',
                                      '&:hover': { color: '#DC2626', backgroundColor: 'rgba(220, 38, 38, 0.1)' }
                                    }}
                                  >
                                    <FaTrash size={14} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Paper>
                          
                          {/* Arrow for horizontal connection */}
                          {index !== row.length - 1 && (
                            <svg
                              style={{
                                position: 'absolute',
                                top: '50%',
                                ...(rowIndex % 2 === 0 ? {
                                  // Even rows (0, 2, 4...) - right to left
                                  // we should add the widht of the box here
                                  left: 'calc(100% - 32px)',
                                  transform: 'translateY(-50%) rotate(180deg)',
                                  zIndex: index === 0 ? 2 : 3  // Higher z-index for second arrow
                                } : {
                                  // Odd rows (1, 3, 5...) - left to right
                                  right: 'calc(100% - 32px)',
                                  transform: 'translateY(-50%)',
                                  zIndex: 2
                                }),
                                width: '8px',
                                height: '8px'
                              }}
                            >
                              <polygon points="0,0 8,4 0,8" fill="#2563EB" />
                            </svg>
                          )}
                          
                          {/* Arrow for vertical connection - only for last box of each row */}
                          {rowIndex !== rows.length - 1 && index === row.length - 1 && (
                            <svg
                              style={{
                                position: 'absolute',
                                bottom: '8px',
                                left: '50%',
                                width: '8px',
                                height: '8px',
                                transform: 'translate(-50%, 0) rotate(-90deg)',
                                zIndex: 2
                              }}
                            >
                              <polygon points="0,0 8,4 0,8" fill="#2563EB" />
                            </svg>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ));
                })()}
              </Box>
            )}
          </TimelineContainer>
        </Content>

        {/* Add Entry Modal */}
        <Dialog 
          open={showAddModal} 
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Position & Grade Entry</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={newEntry.positionId}
                  onChange={(e) => {
                    const position = positions.find(p => p.id === e.target.value);
                    setNewEntry(prev => ({ 
                      ...prev, 
                      positionId: e.target.value,
                      // Clear grade if new position doesn't require it
                      gradeId: position?.requiresGrade ? prev.gradeId : null
                    }));
                  }}
                  label="Position"
                  onClose={() => {
                    setPositionSearchQuery('');
                    setIsSelectOpen(false);
                  }}
                  onOpen={() => setIsSelectOpen(true)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 450
                      }
                    },
                    // Prevent select from closing when clicking the search field
                    autoFocus: false,
                    disableAutoFocusItem: true,
                    // Disable the automatic keyboard handling
                    disableListWrap: true
                  }}
                >
                  <Box 
                    sx={{ 
                      position: 'sticky', 
                      top: 0, 
                      bgcolor: 'background.paper', 
                      zIndex: 1,
                      p: 1
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search positions..."
                      value={positionSearchQuery}
                      onChange={(e) => setPositionSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        // Prevent select from handling keyboard events
                        e.stopPropagation();
                      }}
                      autoComplete="off"
                      sx={{ 
                        width: '100%',
                        backgroundColor: 'white',
                        '& .MuiInputBase-root': {
                          borderRadius: 1
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaSearch size={14} />
                          </InputAdornment>
                        ),
                      }}
                      autoFocus
                    />
                  </Box>
                  {getFilteredPositionsByOrgUnitType().map(group => [
                    <ListSubheader
                      key={group.orgUnitType.id}
                      sx={{
                        backgroundColor: '#f8f9fa',
                        lineHeight: '32px',
                        borderRadius: '4px',
                        color: '#6b7280',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        position: 'static' // Prevent sticky behavior of subheader
                      }}
                    >
                      {group.orgUnitType.name}
                    </ListSubheader>,
                    ...group.positions.map(position => (
                      <MenuItem 
                        key={position.id} 
                        value={position.id}
                        sx={{
                          pl: 4
                        }}
                      >
                        {position.label}
                      </MenuItem>
                    ))
                  ])}
                  {getFilteredPositionsByOrgUnitType().length === 0 && (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      No positions found
                    </Box>
                  )}
                </Select>
              </FormControl>

              {/* Only show grade select if position requires it */}
              {positions.find(p => p.id === newEntry.positionId)?.requiresGrade && (
                <FormControl fullWidth>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    value={newEntry.gradeId}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, gradeId: e.target.value }))}
                    label="Grade"
                  >
                    {grades.map(grade => (
                      <MenuItem key={grade.id} value={grade.id}>
                        {grade.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <DatePicker
                label="Start Date"
                value={newEntry.startDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                    setNewEntry(prev => ({ ...prev, startDate: newValue }));
                  } else if (newValue === null) {
                    setNewEntry(prev => ({ ...prev, startDate: null }));
                  }
                }}
                format="DD/MM/YYYY"
                shouldDisableDate={disableOverlappingStart(employeeData?.history)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    inputProps: { placeholder: 'DD/MM/YYYY' },
                    helperText: startDateHint(employeeData?.history)
                  }
                }}
              />

              <TextField
                label="Reason (optional)"
                value={newEntry.reason}
                onChange={(e) => setNewEntry(prev => ({ ...prev, reason: e.target.value }))}
                helperText="Recorded on the position amendment — e.g. the addendum's wording"
                fullWidth
                sx={{ mt: 2 }}
              />

              {addError && (
                <Alert severity="error" onClose={() => setAddError(null)} sx={{ mt: 2 }}>
                  {addError}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <HeaderButton 
                  onClick={handleCloseModal}
                  sx={{ backgroundColor: '#E2E8F0', color: '#64748B' }}
                >
                  Cancel
                </HeaderButton>
                <HeaderButton 
                  onClick={handleAddEntry}
                  disabled={!newEntry.positionId || !newEntry.startDate || (positions.find(p => p.id === newEntry.positionId)?.requiresGrade && !newEntry.gradeId)}
                  sx={{
                    '&.Mui-disabled': {
                      backgroundColor: '#E2E8F0',
                      color: '#94A3B8'
                    }
                  }}
                >
                  Add Entry
                </HeaderButton>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Edit Position Modal */}
        <EditPositionModal
          isOpen={editPositionModalOpen}
          onClose={() => {
            setEditPositionModalOpen(false);
            setPositionToEdit(null);
          }}
          employeeId={id}
          positionEntry={positionToEdit}
          onSuccess={handlePositionEditSuccess}
        />

        {/* Edit Grade Modal */}
        <EditGradeModal
          isOpen={editGradeModalOpen}
          onClose={() => {
            setEditGradeModalOpen(false);
            setGradeToEdit(null);
          }}
          employeeId={id}
          gradeEntry={gradeToEdit}
          positionLabel={gradeToEdit?.positionLabel}
          onSuccess={handleGradeEditSuccess}
        />

        {/* Add Grade Modal */}
        <AddGradeModal
          isOpen={addGradeModalOpen}
          onClose={() => {
            setAddGradeModalOpen(false);
            setPositionForNewGrade(null);
          }}
          employeeId={id}
          positionEntry={positionForNewGrade}
          existingGradeIds={positionForNewGrade?.gradeHistory?.map(g => g.grade?.id) || []}
          onSuccess={handleAddGradeSuccess}
        />

        {/* Delete Position Confirmation */}
        <ConfirmationModal
          isOpen={deletePositionConfirmOpen}
          onClose={() => {
            setDeletePositionConfirmOpen(false);
            setPositionToDelete(null);
          }}
          onConfirm={handleDeletePositionConfirm}
          title="Delete Position History"
          message={dayjs(positionToDelete?.startDate).isAfter(dayjs(), 'day')
            ? `This position change is scheduled for ${formatDateOnly(positionToDelete?.startDate, 'DD/MM/YYYY')} — deleting it CANCELS the scheduled change: the current position stays in effect and the previous entry is extended back. Continue?`
            : `Are you sure you want to delete the position "${positionToDelete?.position?.label}"? This will also delete all associated grade history entries.`}
          actionButtonText="Delete"
          actionButtonColor="#DC2626"
        />

        {/* Delete Grade Confirmation */}
        <ConfirmationModal
          isOpen={deleteGradeConfirmOpen}
          onClose={() => {
            setDeleteGradeConfirmOpen(false);
            setGradeToDelete(null);
          }}
          onConfirm={handleDeleteGradeConfirm}
          title="Delete Grade History"
          message={`Are you sure you want to delete the grade "${gradeToDelete?.grade?.label}"?`}
          actionButtonText="Delete"
          actionButtonColor="#DC2626"
        />
      </Container>
    </LocalizationProvider>
  );
};

export default EmployeePositionHistory; 